import { useEffect, useMemo, useState } from "react";
import type { Client, Order } from "../../data/schemaTypes";
import { useMastersStore } from "../../data/mastersStore";
import { Badge, Button } from "../../components/ui";
import { LocationMiniMap } from "../../components/LocationMiniMap";
import { api } from "../../data/api";

type Props = {
  client: Client | null;
  onClose: () => void;
  onEdit: () => void;
};

// Drawer lateral con el detalle del cliente: contacto, dirección
// primaria y sucursales. Es read-only en V1 — la acción "Editar" abre el
// modal de edición.
export function ClientDetailDrawer({ client, onClose, onEdit }: Props) {
  // Subscribimos al array base (referencia estable) y filtramos con
  // useMemo. Antes pasábamos `selectAddressesForClient` directo al hook;
  // ese selector llama `.filter()` y devuelve un array nuevo cada render
  // → zustand v5 detecta cambio → re-render infinito (warning de
  // "getSnapshot should be cached"). El patrón base+useMemo evita el loop.
  const allAddresses = useMastersStore((s) => s.clientAddresses);
  const addresses = useMemo(
    () =>
      client ? allAddresses.filter((a) => a.client_id === client.id) : [],
    [allAddresses, client],
  );

  // Fallback de coords: si las direcciones del cliente no tienen lat/lon
  // cargado (el import del Excel solo persiste coords en `orders.delivery_lat`,
  // no necesariamente en `client_addresses`), tomamos la orden más
  // reciente del cliente como fuente. Se hace fetch lazy al abrir el
  // drawer; ResetOnClose lo limpia para no mezclar entre clientes.
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  useEffect(() => {
    if (!client) {
      setRecentOrders([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const list = await api.listOrders({ clientId: client.id, limit: 20 });
        if (!cancelled) setRecentOrders(list);
      } catch {
        if (!cancelled) setRecentOrders([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client]);

  if (!client) return null;

  return (
    <div
      className="fixed inset-0 z-[2000] bg-ink/40 flex justify-end"
      onClick={onClose}
    >
      <aside
        className="w-full max-w-md bg-card border-l border-border h-full overflow-auto shadow-card-hover"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <div className="text-xs font-mono text-primary">{client.code}</div>
            <h2 className="text-base font-semibold text-ink">{client.name}</h2>
          </div>
          <button onClick={onClose} className="text-sub hover:text-ink text-xs">
            cerrar ✕
          </button>
        </header>

        <section className="p-4 space-y-3 border-b border-border">
          <Row label="Tipo de negocio" value={client.business_type ?? "—"} />
          <Row
            label={client.tax_id_type ?? "ID"}
            value={client.tax_id ?? "—"}
          />
          <Row label="Contacto" value={client.contact_name ?? "—"} />
          <Row label="Teléfono" value={client.contact_phone ?? "—"} mono />
          <Row label="Email" value={client.email ?? "—"} />
          <Row label="Estado" valueNode={<Badge variant={client.active ? "delivered" : "pending"}>{client.active ? "Activo" : "Inactivo"}</Badge>} />
        </section>

        <section className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[11px] uppercase tracking-wide text-sub">
              Direcciones de entrega ({addresses.length})
            </h3>
            <button
              className="text-xs text-primary hover:underline disabled:opacity-40"
              disabled
              title="Próximamente"
            >
              + Agregar
            </button>
          </div>
          <ul className="space-y-2">
            {addresses.map((a) => (
              <li
                key={a.id}
                className={`border rounded-md p-3 ${a.is_primary ? "border-primary/40 bg-primary/5" : "border-border bg-page"}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-ink">
                    {a.label}
                  </span>
                  {a.is_primary && (
                    <Badge variant="in-route">Principal</Badge>
                  )}
                </div>
                <div className="text-sm text-ink">{a.address}</div>
                <div className="text-xs text-sub mt-0.5">
                  {[a.district, a.city].filter(Boolean).join(" · ")}
                </div>
                {a.lat != null && a.lon != null && (
                  <div className="text-[11px] text-sub font-mono mt-1">
                    {a.lat.toFixed(4)}, {a.lon.toFixed(4)}
                  </div>
                )}
              </li>
            ))}
            {addresses.length === 0 && (
              <li className="text-sm text-sub italic">
                Sin direcciones registradas.
              </li>
            )}
          </ul>
        </section>

        {/* Mapa del negocio — prioridad de fuentes de coordenadas:
            1) Dirección marcada como principal con lat/lon cargado.
            2) Cualquier dirección del cliente con lat/lon.
            3) Última orden del cliente (delivery_lat/lon) — fallback
               importante porque el import del Excel SOMAR persiste las
               coordenadas en `orders`, no necesariamente en
               `client_addresses`. Sin este fallback el mapa quedaría
               vacío para la mayoría de clientes importados. */}
        <section className="p-4 border-t border-border">
          {(() => {
            const primary = addresses.find((a) => a.is_primary && a.lat != null && a.lon != null);
            const anyWithCoords = addresses.find((a) => a.lat != null && a.lon != null);
            const fromAddress = primary ?? anyWithCoords;
            // Orden más reciente con coords (placed_at desc).
            const recentWithCoords = recentOrders
              .filter((o) => o.delivery_lat != null && o.delivery_lon != null)
              .sort((a, b) => (b.placed_at ?? "").localeCompare(a.placed_at ?? ""))[0];

            const lat = fromAddress?.lat ?? recentWithCoords?.delivery_lat ?? null;
            const lon = fromAddress?.lon ?? recentWithCoords?.delivery_lon ?? null;
            const subLabel = fromAddress
              ? [fromAddress.address, fromAddress.district, fromAddress.city].filter(Boolean).join(" · ")
              : recentWithCoords?.delivery_address
                ? `${recentWithCoords.delivery_address} · (ubicación tomada de la orden ${recentWithCoords.code})`
                : undefined;

            return (
              <LocationMiniMap
                label="Ubicación del negocio"
                lat={lat}
                lon={lon}
                subLabel={subLabel}
                height={220}
              />
            );
          })()}
        </section>

        <footer className="p-4 border-t border-border flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cerrar
          </Button>
          <Button variant="primary" size="sm" onClick={onEdit}>
            Editar
          </Button>
        </footer>
      </aside>
    </div>
  );
}

function Row({
  label,
  value,
  valueNode,
  mono,
}: {
  label: string;
  value?: string;
  valueNode?: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-3 text-sm items-baseline">
      <span className="text-[11px] uppercase tracking-wide text-sub">{label}</span>
      <span className={`text-ink ${mono ? "font-mono text-xs" : ""}`}>
        {valueNode ?? value}
      </span>
    </div>
  );
}
